import { Plugin, Command } from 'ckeditor5';

// Defined constants for model attributes and element names
const FONT_SIZE = 'fontSize'; // Standard CKEditor model attribute for font size
const CC_FONT_SIZE = 'ccfontsize'; // Custom HTML attribute for the view, for robust restoration
const LIST_FONT_SIZE = 'listFontsize'; // Model attribute allowed on extended listItem elements
const LIST_ITEM = 'listItem'; // Model name for list items
const ELEMENT_LI = 'li'; // View name for list items

/**
 * Custom command to apply font size to list items.
 * This command encapsulates the logic to apply font size either to the entire
 * list item (<li>) or to use the fallback to the standard FontSize command.
 */
class ApplyListFontSizeCommand extends Command {
  /**
   * Executes the command to apply font size.
   *
   * @param {Object} options Options for the command.
   * @param {string} options.value The name of the font size (e.g., 'small', 'default', 'huge').
   */
  execute(options) {
    const editor = this.editor;
    const sizeName = options.value;

    const { selection } = editor.editing.view.document; // Current view selection
    const modelSelection = editor.model.document.selection; // Current model selection

    const modelElementStart = modelSelection.getFirstPosition().parent; // Model element at the start of the selection
    const modelElementEnd = modelSelection.getLastPosition().parent; // Model element at the end of the selection

    // Checks if it's a zero-length selection (cursor) within an empty element.
    const isZeroLengthSelection = selection.focus.parent.is('element') && selection.focus.parent.isEmpty;
    // Checks if the start and end of the model selection are within the same element.
    const isSameModelElement = modelElementStart === modelElementEnd;

    /**
     * Finds the closest ancestor listItem model element for a given element.
     *
     * @param {module:engine/model/element~Element|module:engine/model/documentfragment~DocumentFragment} modelElement The model element to check.
     * @returns {module:engine/model/element~Element|null} The ancestor listItem element or null.
     */
    const findModelAncestorListItem = (modelElement) => {
      let parent = modelElement;
      while (parent && !parent.is('documentFragment')) {
        if (parent.is('element', LIST_ITEM)) {
          return parent;
        }
        parent = parent.parent;
      }
      return null;
    };

    editor.model.change(writer => {
      // Scenario 1: Cursor in a list item, or selection entirely within one list item.
      if (isZeroLengthSelection || (isSameModelElement && findModelAncestorListItem(modelElementStart))) {
        // The font size is applied to the entire list item, and all inner font sizes are cleared.
        const currentModelElement = modelSelection.focus.parent; // Could be a paragraph inside a listItem
        const parentListItem = findModelAncestorListItem(currentModelElement);

        if (parentListItem) {
          writer.setAttribute(LIST_FONT_SIZE, `text-${sizeName}`, parentListItem);
          this._clearFontSizeInElement(writer, parentListItem); // Cleans up nested font sizes
        } else {
          // Fallback to the standard FontSize command if the selection is not within a list item.
          editor.execute(FONT_SIZE, options);
        }
      } else {
        // Scenario 2: Selection spans across multiple elements (list items or other blocks).
        // Or the selection is outside a list item.
        let hasAppliedToListItems = false;
        for (const range of modelSelection.getRanges()) {
          for (const { item } of range.getWalker()) {
            if (item.is('element', LIST_ITEM)) {
              // If a list item is found in the selection, apply the font size and clean up.
              writer.setAttribute(LIST_FONT_SIZE, `text-${sizeName}`, item);
              this._clearFontSizeInElement(writer, item);
              hasAppliedToListItems = true;
            }
          }
        }

        // If no list items were affected, the standard FontSize command is applied.
        if (!hasAppliedToListItems) {
          editor.execute(FONT_SIZE, options);
        }
      }
    });
  }

  /**
   * Clears the 'fontSize' attribute in the child elements of the given model element.
   * This prevents nested inline font size styles generated by the standard FontSize plugin.
   *
   * @param {module:engine/model/writer~Writer} writer The model writer to modify the model.
   * @param {module:engine/model/element~Element} modelElement The model element whose children should be cleaned.
   */
  _clearFontSizeInElement(writer, modelElement) {
    for (const child of modelElement.getChildren()) {
      if (child.is('element') && child.hasAttribute(FONT_SIZE)) {
        writer.removeAttribute(FONT_SIZE, child);
      }
      // Recursively clear for deeper nested elements if necessary.
      if (child.is('element') && child.childCount > 0) {
        this._clearFontSizeInElement(writer, child);
      }
    }
  }
}

/**
 * The FontSize plugin for CKEditor 5.
 * It extends the 'listItem' schema and introduces a custom
 * 'ccfontsize' attribute for the model and view.
 */
export default class FontSize extends Plugin {
  /**
   * Initializes the plugin.
   */
  init() {
    const editor = this.editor;

    // Registers the custom command that encapsulates the core logic.
    editor.commands.add('applyListFontSize', new ApplyListFontSizeCommand(editor));

    // Configures the attribute conversions for list items.
    this.allowAttributeCcFontSizeInList(editor);
    // Creates the custom font size dropdown.
    this.customFontSizeDropDown(editor);
  }

  /**
   * Finds the closest ancestor element node in the view tree with the specified name.
   * (Note: This method was replaced by `findModelAncestorListItem` in the new `ApplyListFontSizeCommand`,
   * as the logic now primarily operates on the model.)
   *
   * @param {module:engine/view/node~Node|module:engine/view/documentfragment~DocumentFragment} element The starting node.
   * @param {string} name The tag name of the parent element to search for (e.g., 'li').
   * @returns {module:engine/view/element~Element|null} The ancestor element or null.
   */
  findAncestor(element, name) {
    let parent = element.parent;
    while (parent && !parent.is('documentFragment')) {
      if (parent.name === name) {
        return parent;
      }
      parent = parent.parent;
    }
    return null;
  }

  /**
   * Configures the custom font size dropdown.
   * It utilizes CKEditor's existing 'fontSize' dropdown, but its 'execute' behavior
   * is overridden to use the custom 'applyListFontSize' command.
   *
   * @param {module:core/editor/editor~Editor} editor The editor instance.
   */
  customFontSizeDropDown(editor) {
    editor.ui.componentFactory.add('fontSizeDropdown', () => {
      // Uses the original fontSize button - we only change its behavior.
      const dropdownView = editor.ui.componentFactory.create('fontSize'); // TODO: translate menu title?

      // Overrides the default 'execute' listener to use our custom command.
      dropdownView.on('execute', ({ source }) => {
        const sizeName = source.commandParam ?? 'default';
        editor.execute('applyListFontSize', { value: sizeName });
      });

      return dropdownView;
    });
  }

  /**
   * Configures how the 'listFontsize' attribute is handled in the model and view.
   * This includes schema extension, downcast (model to view), and upcast (view to model) conversions.
   *
   * @param {module:core/editor/editor~Editor} editor The editor instance.
   */
  allowAttributeCcFontSizeInList(editor) {
    // Extends the schema to allow the 'listFontsize' attribute on listItem elements.
    editor.model.schema.extend(LIST_ITEM, { allowAttributes: LIST_FONT_SIZE });

    // --- Downcast Converters (Model to View) ---

    // 1. Converts the LIST_FONT_SIZE model attribute to the 'class' attribute of the view listItem.
    editor.conversion.for('downcast').attributeToAttribute({
      model: {
        name: LIST_ITEM,
        key: LIST_FONT_SIZE
      },
      view: modelAttributeValue => {
        // Ensures that a default value is always provided if the model attribute is null/undefined.
        const value = modelAttributeValue ?? 'text-default';
        return {
          key: 'class',
          value,
        };
      }
    });

    // 2. Converts the LIST_FONT_SIZE model attribute to the custom 'ccfontsize' attribute of the view listItem.
    // This is crucial for robust upcasting later, especially for newly saved data.
    editor.conversion.for('downcast').attributeToAttribute({
      model: {
        name: LIST_ITEM,
        key: LIST_FONT_SIZE
      },
      view: modelAttributeValue => {
        const value = modelAttributeValue ?? 'text-default';
        return {
          key: CC_FONT_SIZE, // This will output ccfontsize="text-..." in the HTML
          value,
        };
      }
    });

    // 3. Handles insertion of new list items: If listFontsize is not set in the model,
    // it applies 'text-default'. This listener should only fire if the attribute
    // is truly missing on insertion (after upcasting has run).
    editor.conversion.for('downcast').add(dispatcher => {
      dispatcher.on('insert:listItem', (evt, { item }, conversionApi) => {
        // Sets the default only if the attribute is not already present on the model item.
        // This prevents overwriting during upcasting if the attribute was already read from HTML.
        if (item.getAttribute(LIST_FONT_SIZE) === undefined) {
          editor.model.change(modelWriter => {
            modelWriter.setAttribute(
              LIST_FONT_SIZE,
              'text-default',
              item
            );
          });
        }
      });
    });

    // --- Upcast Converters (View to Model) ---

    // 1. Primary Upcast: Converts the custom 'ccfontsize' attribute from the view li
    // to the LIST_FONT_SIZE model attribute. This converter processes data
    // that was correctly saved with the 'ccfontsize' attribute.
    editor.conversion.for('upcast').attributeToAttribute({
      model: {
        name: LIST_ITEM,
        key: LIST_FONT_SIZE,
        value: viewElement => viewElement.getAttribute(CC_FONT_SIZE), // Retrieves the value directly from ccfontsize
      },
      view: {
        name: ELEMENT_LI,
        key: CC_FONT_SIZE, // Triggers this converter when <li> has the 'ccfontsize' attribute
      },
      converterPriority: 'high' // High priority, so it's checked first if 'ccfontsize' is present.
    });

    // 2. Fallback Upcast: Converts the 'class' attribute from the view li to the LIST_FONT_SIZE model attribute.
    // This converter specifically handles older HTML that only has the 'class="text-..."' attribute
    // but lacks the 'ccfontsize' attribute.
    editor.conversion.for('upcast').attributeToAttribute({
      model: {
        name: LIST_ITEM,
        key: LIST_FONT_SIZE,
        value: viewElement => {
          const classAttribute = viewElement.getAttribute('class');
          if (classAttribute) {
            // Uses a regular expression to extract the specific font size class (e.g., 'text-tiny').
            // Ensures this regex matches all possible font sizes your dropdown supports.
            // Example sizes: 'tiny', 'small', 'default', 'big', 'huge' or '12px', '14px', etc.
            const match = classAttribute.match(/text-(?:[a-z]+|\d+px)/);
            if (match && match[0]) {
              return match[0]; // Returns the full class name, e.g., 'text-tiny'
            }
          }
          // Returns undefined if no matching class is found. This allows other converters
          // or the 'insert:listItem' downcast to set a default if necessary.
          return undefined;
        },
      },
      view: {
        name: ELEMENT_LI,
        key: 'class', // Triggers this converter when <li> has a 'class' attribute.
        // The 'value' function then filters for relevant classes.
      },
      // Normal priority, so it runs after the 'ccfontsize' converter.
      // If 'ccfontsize' was already processed and set the model attribute, this one won't override it.
      converterPriority: 'normal'
    });

    // --- Model Post-Fixer ---
    // This post-fixer ensures that list items that are newly created or
    // converted from other block elements receive the 'text-default' class,
    // if no specific font size has been assigned yet.
    editor.model.document.registerPostFixer(writer => {
      const changes = editor.model.document.differ.getChanges();
      let madeChanges = false;

      for (const entry of changes) {
        // Case 1: An element is converted into a list item (listType/listIndent attributes added).
        if (entry.type === 'attribute' &&
          (entry.attributeKey === 'listType' || entry.attributeKey === 'listIndent') &&
          entry.attributeNewValue !== undefined // The attribute was set (not removed or changed)
        ) {
          // Get the affected element (whose attributes have changed).
          const element = entry.range.start.parent;
          if (element && element.is('element', LIST_ITEM) && !element.hasAttribute(LIST_FONT_SIZE)) {
            writer.setAttribute(LIST_FONT_SIZE, 'text-default', element);
            madeChanges = true;
          }
        }
        // Case 2: A new list item is explicitly inserted (e.g., by pressing Enter in a list).
        else if (entry.type === 'insert' && entry.name === LIST_ITEM) {
          const item = entry.position.nodeAfter;
          if (item && item.is('element', LIST_ITEM) && !item.hasAttribute(LIST_FONT_SIZE)) {
            writer.setAttribute(LIST_FONT_SIZE, 'text-default', item);
            madeChanges = true;
          }
        }
      }
      return madeChanges;
    });
  }
}
